import { Add, Edit } from "@mui/icons-material";
import {
    Button,
    IconButton,
    Switch,
    Tooltip
} from "@mui/material";
import { useFormik } from "formik";
import moment from "moment";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { API_URLS } from "../../config/APIUrls";
import axiosInstance from "../../config/axios";
import CustomDialog from "../../Shared/CustomDialogBox";
import CustomFilter from "../../Shared/CustomForFiler";
import CustomTable from "../../Shared/CustomTable";
import CustomToPagination from "../../Shared/CustomToPagination";
import * as Yup from "yup";

const ZoneManagerMaster = () => {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [page, setPage] = useState(1);

    const initialValue = {
        search: '',
        start_date: '',
        end_date: '',
    };

    const formik = useFormik({
        initialValues: initialValue,
        enableReinitialize: true,
    });

    const { loading, data: zoneManagerList } = useQuery(
        ["get_zone_manager", page, formik.values.search, formik.values.start_date, formik.values.end_date],
        () => axiosInstance.get(API_URLS.get_zone_manager, {
            params: {
                search: formik.values.search,
                start_date: formik.values.start_date,
                end_date: formik.values.end_date,
                page: page,
                count: 10,
            },
        }),
        {
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false
        }
    );

    const zoneManagers = zoneManagerList?.data?.response || [];

    const fk = useFormik({
        initialValues: {
            name: "",
            m03_mob: "",
            m03_email: "",
            m03_pass: "",
            // desg: ""
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Name is required"),
            m03_mob: Yup.string()
                .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
                .required("Mobile number is required"),
            m03_email: Yup.string()
                .email("Invalid email address")
                .required("Email is required"),
            m03_pass: Yup.string().required("Password is required"),
        }),
        onSubmit: async (values) => {
            if (editData) return await updateZoneManager(values);
            else return await createZoneManager(values);
        },
    });

    const createZoneManager = async (values) => {
        try {
            const reqBody = {
                name: values.name,
                m03_mob: values.m03_mob,
                m03_email: values.m03_email,
                m03_pass: values.m03_pass,

            };
            const res = await axiosInstance.post(API_URLS.create_zone_manager, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_zone_manager");
                handleClose();
            }
        } catch (e) {
            toast.error("Something went wrong while creating");
        }
    };

    const updateZoneManager = async (values) => {
        try {
            const reqBody = {
                m01_reg_id: editData.m01_reg_id,
                m01_full_name: values.name,
                m03_mob: values.m03_mob,
                m03_email: values.m03_email,
                m03_pass: values.m03_pass,
                m01_status: String(editData?.m03_lgn_status)
            };
            const res = await axiosInstance.post(API_URLS.update_zone_manager, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_zone_manager");
                handleClose();
            }
        } catch (e) {
            toast.error("Something went wrong while updating");
        }
    };

    const toggleStatus = async (row, checked) => {
        try {
            const reqBody = {
                m03_reg_id: row.m01_reg_id,
                // m03_lgn_status: checked ? "1" : "0"
            };
            const res = await axiosInstance.post(API_URLS.update_master_login_status, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_zone_manager");
            }
        } catch (e) {
            toast.error("Failed to update status");
        }
    };


    const handleExport = async () => {
        try {
            const res = await axiosInstance.get(API_URLS.zone_manager_excel, {
                search: formik.values.search || "",
                start_date: formik.values.start_date || "",
                end_date: formik.values.end_date || "",
            })
            const fileUrl = res?.data?.response?.url;
            window.open(fileUrl, "_blank")
        }
        catch (e) {
            console.log("Something went wrong")
        }
    }

    const handleOpen = () => {
        fk.resetForm();
        setEditData(null);
        setOpen(true);
    };

    const handleEdit = (row) => {
        setEditData(row);
        fk.setValues({
            name: row.m01_full_name,
            m03_mob: row.m03_mob,
            m03_email: row.m03_email,
            m03_pass: row.m03_pass,
        });
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditData(null);
    };

    const tablehead = [
        "S.No",
        "Name",
        "Mobile",
        "Email",
        "Designation",
        "Password",
        "Status",
        "Actions",
        "Date",
    ];

    const tablerow = zoneManagers?.data?.map((item, index) => [
        <span>{index + 1}</span>,
        <span>{item.m01_full_name || "--"}</span>,
        <span>{item.m03_mob || "--"}</span>,
        <span>{item.m03_email || "--"}</span>,
        <span>{item.m02_desig_name || "--"}</span>,
        <span>{item.m03_pass || "--"}</span>,
        <Switch
            checked={item?.m03_lgn_status === 1}
            onChange={(e) => toggleStatus(item, e.target.checked)}
            color="success"
        />,
        <div className="flex justify-center gap-3">
            <Tooltip title="Edit">
                <IconButton onClick={() => handleEdit(item)}>
                    <Edit className="text-blue-600" />
                </IconButton>
            </Tooltip>
        </div>,
        <span>{moment(item?.m01_created_at).format("DD-MM-YYYY")}</span>
    ]);

    return (
        <>
            <div className="mx-5">
                <div className="flex justify-between items-center p-4 ">
                    <p className="font-bold text-xl">Block Manager Master</p>
                <Button variant="contained" className="!bg-[#00008b]" 
                        startIcon={<Add />}
                        onClick={handleOpen}
                  
                    >
                        Add 
                    </Button>
                </div>

                <CustomFilter
                    formik={formik}
                    onFilter={() => {
                        setPage(1);
                        queryClient.invalidateQueries("get_zone_manager");
                    }}
                    onClear={() => {
                        formik.resetForm();
                        setPage(1);
                        queryClient.invalidateQueries("get_zone_manager");
                    }}
                    onExport={handleExport}
                />

                <CustomTable
                    tablehead={tablehead}
                    tablerow={tablerow}
                    isLoading={loading}
                />

                <CustomToPagination
                    setPage={setPage}
                    page={page}
                    data={zoneManagers}
                />
            </div>

            <CustomDialog
                open={open}
                onClose={handleClose}
                onSubmit={fk.handleSubmit}
                title={editData ? "Edit Block Manager" : "Add Block Manager"}
                formik={fk}
                fields={[
                    { name: "name", label: "Name" },
                    { name: "m03_mob", label: "Mobile", type: "number" },
                    { name: "m03_email", label: "Email", type: "email" },
                    { name: "m03_pass", label: "Password" },
                ]}
            />

        </>
    );
};

export default ZoneManagerMaster;
